<!--
  LastCollectedLine — the second dated fact: "the club last got this on {date},
  from {supplier}, for {price}".

  It is the answer to the question a member actually asks before saving a post —
  "do we ever get this, and what does it cost?" — which no other surface answers.
  Independent of the availability badge: a product can carry both (out of stock
  now, and here is what it last cost), either, or neither.

  `price` is the gross UNIT price, not a line total, and Poola lines are excluded
  backend-side — a flash-sale deal price is not a repeatable price and must never
  be shown as one.

  Absent = renders nothing. The parent decides that by not mounting this; the
  guard itself lives in utils/productAvailability.js.
-->
<template>
  <p class="last-collected">
    <f7-icon f7="clock" size="14" class="last-collected__icon"></f7-icon>
    <span>{{ label }}</span>
  </p>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { formatAvailabilityDate } from '@/utils/productAvailability.js';

const props = defineProps({
  /** A normalised fact from `lastCollectedFact()` — never a raw attribute. */
  fact: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();

// Two keys rather than one with a conditional: interpolating an empty supplier
// would leave a dangling «من» separator in front of nothing.
const label = computed(() => {
  const date = formatAvailabilityDate(props.fact.at, locale.value);
  const price = formatCurrency(props.fact.priceCents);

  return props.fact.supplier
    ? t('product.lastCollected.full', { date, supplier: props.fact.supplier, price })
    : t('product.lastCollected.noSupplier', { date, price });
});
</script>

<style scoped>
.last-collected {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 6px 12px 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--poolit-text-secondary);
}

.last-collected__icon {
  flex-shrink: 0;
  opacity: 0.7;
}
</style>
